import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Music, Search, Play, CheckCircle, Clock, X, Save, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert } from '../design-system';
import { apiPost, apiGet } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const TaskSongOfTheDay = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [previewVideoId, setPreviewVideoId] = useState(null);
  const [reflectionAnswers, setReflectionAnswers] = useState({
    q1: '',
    q2: '',
    q3: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  const previewTimeoutRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const isSubmittingRef = useRef(false);

  // Auto-save functionality
  const saveProgress = useCallback(async () => {
    // Don't auto-save if submitting or if no session
    if (!session?.sessionId || isSubmittingRef.current) return;

    setIsSaving(true);
    try {
      await apiPost(
        '/.netlify/functions/songOfTheDayTaskApi',
        {
          sessionId: session.sessionId,
          selectedVideo: selectedVideo,
          reflectionAnswers: reflectionAnswers
        },
        userInfo?.userId
      );

      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, selectedVideo, reflectionAnswers, user, userInfo?.userId]);

  // Validate videoId format (YouTube video IDs are 11 alphanumeric characters)
  const isValidVideoId = (videoId) => {
    if (!videoId || typeof videoId !== 'string') return false;
    return /^[a-zA-Z0-9_-]{11}$/.test(videoId);
  };

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      return () => {
        // Clear any existing timeout
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
        // Don't schedule auto-save if submitting
        if (isSubmittingRef.current) return;
        
        autoSaveTimeoutRef.current = setTimeout(() => {
          // Double-check we're not submitting before saving
          if (!isSubmittingRef.current) {
            saveProgress();
          }
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing data if session has it
    if (session.sessionData?.selectedVideo) {
      // Validate loaded video data
      const loadedVideo = session.sessionData.selectedVideo;
      if (loadedVideo.videoId && isValidVideoId(loadedVideo.videoId)) {
        setSelectedVideo(loadedVideo);
        setShowSearch(false);
      } else {
        console.warn('Invalid video data in session, ignoring');
      }
    }
    if (session.sessionData?.reflectionAnswers) {
      setReflectionAnswers(session.sessionData.reflectionAnswers);
    }
  }, [session, hasLoadedProgress]);

  // Auto-save when data changes
  useEffect(() => {
    if (hasLoadedProgress && (selectedVideo || reflectionAnswers.q1 || reflectionAnswers.q2 || reflectionAnswers.q3)) {
      debouncedSave();
    }
  }, [selectedVideo, reflectionAnswers, hasLoadedProgress, debouncedSave]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (query) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (query.trim().length === 0) {
            setSearchResults([]);
            return;
          }

          setIsSearching(true);
          try {
            const response = await apiGet(`/.netlify/functions/youtube-search?q=${encodeURIComponent(query)}`);
            setSearchResults(response.videos || []);
          } catch (error) {
            console.error('Error searching YouTube:', error);
            toast.error('Failed to search for songs. Please try again.');
            setSearchResults([]);
          } finally {
            setIsSearching(false);
          }
        }, 500); // 500ms debounce
      };
    })(),
    []
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handlePreview = (videoId) => {
    // Validate videoId format
    if (!isValidVideoId(videoId)) {
      toast.error('Invalid video ID format');
      return;
    }

    // Validate videoId is from search results (prevents manipulation)
    const isValidVideo = searchResults.some(v => v.videoId === videoId);
    if (!isValidVideo) {
      toast.error('Invalid video selection');
      return;
    }
    
    setPreviewVideoId(videoId);
    
    // Optional: Auto-pause after 20 seconds
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    previewTimeoutRef.current = setTimeout(() => {
      // Note: We can't directly control YouTube iframe from outside due to CORS
      // This is just a visual indicator - user can pause manually
      // Removed toast.info as it's not critical and react-hot-toast doesn't have .info()
    }, 20000);
  };

  const handleSelectVideo = (video) => {
    // Validate video object structure
    if (!video || !video.videoId) {
      toast.error('Invalid video data');
      return;
    }

    // Validate videoId format
    if (!isValidVideoId(video.videoId)) {
      toast.error('Invalid video ID format');
      return;
    }

    // Validate video is from search results (prevents manipulation)
    const isValidVideo = searchResults.some(v => v.videoId === video.videoId);
    if (!isValidVideo) {
      toast.error('Invalid video selection');
      return;
    }

    // Validate required video properties
    if (!video.title || !video.thumbnail || !video.channel) {
      toast.error('Incomplete video data');
      return;
    }

    setSelectedVideo(video);
    setShowSearch(false);
    setPreviewVideoId(null);
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    toast.success('Song selected!');
  };

  const handleChangeSong = () => {
    setSelectedVideo(null);
    setShowSearch(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleReflectionChange = (questionId, value) => {
    setReflectionAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleManualSave = async () => {
    await saveProgress();
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    // Prevent multiple simultaneous submissions
    if (isSubmittingRef.current || isSubmitting) {
      return;
    }

    // Validation
    if (!selectedVideo) {
      toast.error('Please select a song before submitting');
      return;
    }
    if (!reflectionAnswers.q1.trim()) {
      toast.error('Please answer the first reflection question');
      return;
    }

    // Cancel any pending auto-saves to prevent race conditions
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    // Set submitting flag immediately (both ref and state)
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    // Capture data at the start to avoid stale data during async operations
    const completionData = {
      selectedVideo: selectedVideo,
      reflectionAnswers: reflectionAnswers,
      completedAt: new Date().toISOString()
    };

    try {
      // Save completion data to session
      await apiPost(
        '/.netlify/functions/songOfTheDayTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        userInfo?.userId
      );

      toast.success('Song of the Day completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
      // Don't call onComplete() on error - let user retry
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
      <Card variant="elevated" padding="medium" className="h-full flex flex-col overflow-hidden">
        {/* Task Header */}
        <div className="mb-4">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <Music className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h1 className="text-base md:text-lg font-semibold text-gray-900 leading-relaxed">
                {task.title || 'Song of the Day'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Pick one song that best describes your day and tell your therapist why
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0 mb-4">
          {/* Section A: Search Songs */}
          {showSearch && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search for a song or artist
              </label>
              
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 z-10 pointer-events-none" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search for a song or artist..."
                  className="!pl-14"
                />
              </div>


              {/* Search Results */}
              {isSearching && (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 text-gray-400 animate-spin mr-2" />
                  <span className="text-gray-500">Searching...</span>
                </div>
              )}

              {!isSearching && searchResults.length > 0 && (
                <div className="space-y-3">
                  {searchResults.map((video) => (
                    <Card key={video.videoId} variant="default" padding="medium" className="hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row items-start space-y-3 sm:space-y-0 sm:space-x-3">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full sm:w-24 h-auto sm:h-16 object-cover rounded flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0 w-full">
                          <h3 className="text-sm font-medium text-gray-900 truncate">{video.title}</h3>
                          <p className="text-xs text-gray-500 mt-1">{video.channel}</p>
                          <div className="flex items-center space-x-2 mt-2">
                            <Button
                              onClick={() => handlePreview(video.videoId)}
                              variant="outline"
                              size="small"
                              className="flex items-center"
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Preview
                            </Button>
                            <Button
                              onClick={() => handleSelectVideo(video)}
                              variant="primary"
                              size="small"
                              className="flex items-center"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Use this
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {!isSearching && searchQuery && searchResults.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Music className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No results found. Try a different search.</p>
                </div>
              )}
            </div>
          )}

          {/* Section B: Selected Song Summary */}
          {selectedVideo && !showSearch && (
            <div className="mb-6">
              <Card variant="default" padding="medium" className="bg-purple-50 border-purple-200">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-3 gap-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <img
                      src={selectedVideo.thumbnail}
                      alt={selectedVideo.title}
                      className="w-full sm:w-20 h-auto sm:h-14 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{selectedVideo.title}</h3>
                      <p className="text-xs text-gray-600">{selectedVideo.channel}</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleChangeSong}
                    variant="ghost"
                    size="small"
                    className="self-start sm:self-auto"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Change song
                  </Button>
                </div>
                <p className="text-xs text-purple-700 font-medium">✓ Song selected</p>
              </Card>
            </div>
          )}

          {/* Section C: Reflection Questions */}
          {selectedVideo && (
            <div className="mb-6 space-y-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Reflection Questions</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Why does this song describe your day? <span className="text-red-500">*</span>
                </label>
                <Input
                  type="textarea"
                  value={reflectionAnswers.q1}
                  onChange={(e) => handleReflectionChange('q1', e.target.value)}
                  placeholder="Share why this song resonates with your day..."
                  rows={4}
                  className="resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Is there a lyric that stands out to you? (Optional)
                </label>
                <Input
                  type="textarea"
                  value={reflectionAnswers.q2}
                  onChange={(e) => handleReflectionChange('q2', e.target.value)}
                  placeholder="Share any lyrics that particularly resonate with you..."
                  rows={3}
                  className="resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  One word for how this song feels for you today (Optional)
                </label>
                <Input
                  type="text"
                  value={reflectionAnswers.q3}
                  onChange={(e) => handleReflectionChange('q3', e.target.value)}
                  placeholder="e.g., hopeful, peaceful, energetic..."
                  maxLength={50}
                />
              </div>
            </div>
          )}

          {/* Helpful Tips */}
          {!selectedVideo && (
            <Alert variant="info">
              <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                <span className="mr-2">💡</span>
                Tips
              </h3>
              <ul className="text-sm text-blue-700 space-y-1.5">
                <li>• Search for a song that matches how your day felt</li>
                <li>• Use the preview button to listen to a snippet before choosing</li>
                <li>• Your progress is automatically saved every 10 seconds</li>
                <li>• You can change your song selection at any time</li>
              </ul>
            </Alert>
          )}
        </div>

        {/* Action Buttons - Fixed at bottom */}
        <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <Button
              onClick={handleManualSave}
              disabled={isSaving}
              variant="ghost"
              size="medium"
              className="flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Progress'}
            </Button>
            {lastSaved && (
              <div className="flex items-center text-xs text-gray-500">
                <Clock className="h-3 w-3 mr-1" />
                Last saved: {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>

          <Button
            onClick={handleComplete}
            disabled={isSubmitting || !selectedVideo || !reflectionAnswers.q1.trim()}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </Card>

      {/* Preview Modal - Fixed bottom corner */}
      {previewVideoId && (
        <div className="fixed bottom-4 right-4 z-50 w-full max-w-sm md:max-w-md">
          <Card variant="elevated" padding="medium" className="shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Preview</h3>
              <Button
                onClick={() => setPreviewVideoId(null)}
                variant="ghost"
                size="small"
                className="p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                src={`https://www.youtube.com/embed/${previewVideoId}?autoplay=1&controls=1&modestbranding=1&rel=0&fs=0&iv_load_policy=3`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={false}
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Summary for patient dashboard
TaskSongOfTheDay.Summary = ({ task, session }) => {
  const hasSong = session?.sessionData?.selectedVideo;
  const hasAnswers = session?.sessionData?.reflectionAnswers;
  const answeredCount = hasAnswers ? Object.values(hasAnswers).filter(a => a && a.trim()).length : 0;
  
  return (
    <div className="song-of-day-summary">
      <div className="flex items-center space-x-2">
        <Music className="h-4 w-4 text-purple-600" />
        <span className="text-xs text-gray-500">
          {hasSong && answeredCount > 0
            ? `Song selected, ${answeredCount} reflection${answeredCount !== 1 ? 's' : ''} answered`
            : hasSong
            ? 'Song selected'
            : 'Not started'}
        </span>
      </div>
    </div>
  );
};

// Results summary for therapist view
TaskSongOfTheDay.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData) return null;

  return (
    <Card variant="elevated" padding="large">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">Song of the Day Results</h4>
      
      <div className="flex flex-col md:flex-row items-start space-y-1 md:space-y-0 md:space-x-2 mb-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Music className="h-5 w-5 text-purple-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="text-lg font-medium text-gray-900">{task.title}</h5>
          <p className="text-sm text-gray-500">
            Completed by {patientName} on {new Date(completionData.completedAt || Date.now()).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Selected Song */}
      {completionData.selectedVideo && (
        <div className="border-t pt-1 mb-1">
          <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Music className="h-4 w-4 mr-1" />
            Selected Song:
          </h6>
          <div className="flex flex-col md:flex-row items-start md:items-start space-y-1 md:space-y-0 md:space-x-2">
            <img
              src={completionData.selectedVideo.thumbnail}
              alt={completionData.selectedVideo.title}
              className="w-full md:w-28 h-auto md:h-20 object-cover rounded flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 break-words">{completionData.selectedVideo.title}</p>
              <p className="text-xs text-gray-500 break-words">{completionData.selectedVideo.channel}</p>
                  {/* Note: This link goes directly to YouTube, bypassing our filters.
                      However, the videoId has been validated and sanitized by backend. */}
                  <a
                    href={`https://www.youtube.com/watch?v=${completionData.selectedVideo.videoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-purple-600 hover:text-purple-700 mt-1 inline-block break-words"
                  >
                    Watch on YouTube →
                  </a>
            </div>
          </div>
        </div>
      )}

      {/* Reflection Answers */}
      {completionData.reflectionAnswers && (
        <div className="border-t pt-1 space-y-1">
          {completionData.reflectionAnswers.q1 && (
            <div>
              <h6 className="text-sm font-medium text-gray-700 mb-1">
                Why does this song describe your day?
              </h6>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {completionData.reflectionAnswers.q1}
                </p>
              </div>
            </div>
          )}
          
          {completionData.reflectionAnswers.q2 && (
            <div>
              <h6 className="text-sm font-medium text-gray-700 mb-1">
                Lyric that stands out:
              </h6>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {completionData.reflectionAnswers.q2}
                </p>
              </div>
            </div>
          )}
          
          {completionData.reflectionAnswers.q3 && (
            <div>
              <h6 className="text-sm font-medium text-gray-700 mb-1">
                One word for how this song feels:
              </h6>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  {completionData.reflectionAnswers.q3}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// Task assignment form for therapist
TaskSongOfTheDay.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  // No custom form needed - return null
  // Therapist can customize the task title/description in the generic form
  return null;
};

export default TaskSongOfTheDay;

